import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Select, MenuItem, Checkbox, ListItemText, OutlinedInput, FormControl, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { trimWithEllipsis } from '@conxai/react-kit';
import palette from '@theme/palette';
import { useSolutionMutations, useSystemsConfig } from '@entities/solution';
export function SystemSelector({ systems, solutionId, fileId, category }) {
    const { t } = useTranslation();
    const { data: systemsConfig = {}, isLoading: configLoading } = useSystemsConfig();
    const { updateSystems } = useSolutionMutations(fileId, solutionId);
    const [selectedSystems, setSelectedSystems] = useState(systems);
    const [isOpen, setIsOpen] = useState(false);
    const availableSystems = systemsConfig[category] || [];
    useEffect(() => {
        setSelectedSystems(systems);
    }, [systems]);
    const handleChange = (event) => {
        const { target: { value } } = event;
        setSelectedSystems(typeof value === 'string' ? value.split(',') : value);
    };
    const handleSave = () => {
        const chosen = selectedSystems.filter(sys => availableSystems.includes(sys));
        // Legacy systems (already on the solution but absent from the current catalog) are shown
        // locked/checked, so preserve them instead of silently dropping them on save.
        const legacy = systems.filter(sys => !availableSystems.includes(sys));
        updateSystems([...chosen, ...legacy]);
        setIsOpen(false);
    };
    const handleOpen = () => {
        setSelectedSystems(systems);
        setIsOpen(true);
    };
    return (_jsx(FormControl, { fullWidth: true, sx: { minWidth: 300 }, children: _jsxs(Box, { display: "flex", alignItems: "center", gap: 1, children: [_jsxs(Typography, { fontSize: "14px", children: [t('System'), ":"] }), _jsxs(Select, { multiple: true, displayEmpty: true, value: selectedSystems, onChange: handleChange, open: isOpen, onOpen: handleOpen, onClose: () => setIsOpen(false), input: _jsx(OutlinedInput, {}), renderValue: selected => {
                        if (selected.length === 0) {
                            return _jsx("em", { children: t('Select systems...') });
                        }
                        return trimWithEllipsis(selected.join(', '), 50);
                    }, MenuProps: { PaperProps: { style: { maxHeight: 300 } } }, sx: {
                        height: '28px',
                        backgroundColor: 'white',
                        fontSize: '12px',
                        minWidth: 320,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        '& .MuiSelect-select': { padding: '4px 8px' }
                    }, children: [configLoading ? (_jsx(MenuItem, { disabled: true, children: _jsx(CircularProgress, { size: 20 }) })) : availableSystems.length > 0 ? (availableSystems.map(name => (_jsxs(MenuItem, { value: name, children: [_jsx(Checkbox, { checked: selectedSystems.includes(name) }), _jsx(ListItemText, { primary: name, primaryTypographyProps: { fontSize: '14px' } })] }, name)))) : (_jsx(MenuItem, { disabled: true, children: _jsx(Typography, { variant: "body2", children: t('No systems available') }) })), systems
                            .filter(sys => !availableSystems.includes(sys))
                            .map(sys => (_jsxs(MenuItem, { value: sys, disabled: true, children: [_jsx(Checkbox, { checked: selectedSystems.includes(sys), disabled: true }), _jsx(ListItemText, { primary: `${sys} (${t('Legacy')})`, primaryTypographyProps: { fontSize: '14px', color: palette.neutral[60] } })] }, sys))), _jsxs(Box, { sx: {
                                position: 'sticky',
                                bottom: 0,
                                backgroundColor: 'white',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 1,
                                p: 1,
                                borderTop: `1px solid ${palette.neutral[90]}`
                            }, onMouseDown: e => e.stopPropagation(), children: [_jsx(Button, { size: "large", variant: "text", onClick: () => setSelectedSystems(systems), children: t('Reset') }), _jsx(Button, { size: "large", variant: "contained", onClick: handleSave, children: t('Save') })] })] })] }) }));
}
